import { cn } from "@gettactic/helpers";
import React from "react";
import { EditorTooltip } from "../map/editor/controls/EditorTooltip";
import type { ShortcutString } from "../map/editor/shortcuts/shortcuts";

export interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "aria-label"> {
  /** Accessible name; also used as the tooltip message. */
  label: string;
  shortcut?: ShortcutString;
  isActive?: boolean;
  /** Style hover state with the destructive token (e.g. delete actions). */
  isDestructive?: boolean;
  size?: "sm" | "md";
  /** Skip the tooltip wrapper (e.g. when composed inside another trigger). */
  hasTooltip?: boolean;
}

/**
 * Standard editor icon button: fixed square hit area, tooltip + aria-label
 * always present, accent-tinted active state.
 */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      label,
      shortcut,
      isActive,
      isDestructive,
      size = "md",
      hasTooltip = true,
      className,
      children,
      ...rest
    },
    ref
  ) => {
    const button = (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        aria-pressed={isActive}
        className={cn(
          "flex flex-none items-center justify-center rounded-md text-neutral-600 transition-colors",
          size === "md" ? "h-8 w-8" : "h-7 w-7",
          isDestructive
            ? "hover:bg-red-50 hover:text-red-600"
            : "hover:bg-neutral-100 hover:text-neutral-900",
          isActive && "bg-blue-50 text-blue-700 hover:bg-blue-50",
          "disabled:pointer-events-none disabled:opacity-40",
          className
        )}
        {...rest}
      >
        {children}
      </button>
    );

    if (!hasTooltip) return button;
    return (
      <EditorTooltip message={label} shortcut={shortcut} asChild>
        {button}
      </EditorTooltip>
    );
  }
);
IconButton.displayName = "IconButton";
